import { Box, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { AnimatedNumberText } from '@/components/AnimatedNumber';
import { PrzyciskDowodu, type Dowod } from '@/components/SzufladaDowodu';
import { Pierscien } from '@/components/wykresy/Wykresy';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * PŁYTA GŁÓWNA — jedna liczba jako grafika
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Zaakceptowany kierunek C z konceptu R5-15-009. Zasada, na której to stoi: NA EKRANIE MOŻE BYĆ
 * TYLKO JEDNA PŁYTA. Gdy są dwie, przestaje działać hierarchia i wracamy do siatki równorzędnych
 * kafelków — czyli do ekranu, na którym nic nie jest ważne, bo wszystko jest tak samo duże.
 *
 * Liczba jest tu ustawiona w stopniu, który da się przeczytać z drugiego końca pokoju. To nie
 * efekciarstwo: właściciel zwykle nie „analizuje" pulpitu, tylko rzuca na niego okiem między
 * jedną a drugą sprawą. Jeżeli w tej jednej sekundzie nie odczyta najważniejszej liczby,
 * ekran nie wykonał swojego zadania.
 *
 * PODSTAWA I ŹRÓDŁO STOJĄ PRZY LICZBIE, nie w przypisie — liczba bez podstawy to nie liczba,
 * tylko wrażenie.
 */

export function PlytaGlowna({
  etykieta,
  wartosc,
  opis,
  zrodlo,
  okno,
  dowod,
  stopka,
  boczne,
  pierscien,
  trend,
  kartaId,
  dowodId,
}: {
  etykieta: string;
  /** Sformatowana wartość albo null, gdy jej NIE ZNAMY. Null renderuje „brak danych", nie zero. */
  wartosc: string | null;
  opis?: ReactNode;
  zrodlo: string;
  okno: string;
  dowod?: Dowod;
  stopka?: ReactNode;
  boczne?: ReactNode;
  /*
   * WARIANT Z PIERŚCIENIEM — dla metryk, które są UDZIAŁEM, nie kwotą.
   *
   * Makieta rysuje punktualność jako pierścień, a nie jako liczbę w wielkim stopniu, i ma
   * rację: „69,2%" bez tarczy nie mówi, ile brakuje do całości, a przy udziale to jest
   * pierwsze pytanie, które pada. Kwota takiej tarczy nie ma, bo nie ma maksimum.
   *
   * Gdy podany, ZASTĘPUJE wielką liczbę — dwie liczby-bohaterowie na jednej płycie znoszą
   * hierarchię, dla której płyta w ogóle istnieje.
   */
  pierscien?: { wartosc: number | null; etykieta: string };
  /*
   * PASEK ZMIANY — makieta stawia go POD liczbą, w jednym wierszu z „Skąd ta liczba":
   * kierunek, wielkość zmiany, iskierka i zdanie, WOBEC CZEGO liczona.
   *
   * Ostatni człon jest tu najważniejszy. Sama strzałka w dół nie znaczy nic, dopóki nie
   * wiadomo, z czym porównujemy — a „−3%" wobec wczoraj i wobec analogicznego dnia tygodnia
   * to dwa różne zdania o firmie. Dlatego to jest ReactNode, a nie liczba: ekran ma podać
   * podstawę porównania, nie ładny procent.
   */
  trend?: ReactNode;
  /**
   * Stabilny identyfikator karty dla walidacji DOM (R5-15).
   *
   * Dlaczego atrybut, a nie dopasowanie po etykiecie: sprawdzenie 100 etykiet z inwentarza
   * pokazało, że tylko 66 rozstrzyga się do jednego miejsca. „Wartość wg cennika" występuje
   * cztery razy na jednym ekranie, „Zamówienia w oknie" w sześciu plikach, a „przed czasem"
   * powtarza się raz na każdą szkołę w rankingu. Alternatywą było kotwiczenie po klasach MUI
   * i wartościach stylów — narzędzie, które działa dziś i zaczyna cicho kłamać po pierwszym
   * refaktorze wyglądu.
   *
   * Atrybut nie zmienia wyglądu ani zachowania. Jest wyłącznie adresem.
   */
  kartaId?: string;
  /**
   * Identyfikator SZUFLADY DOWODU tej płyty — osobny adres, nie ten sam co `kartaId`.
   *
   * Dlaczego dwa propy, a nie jeden: płyta i jej dowód to w inwentarzu dwie różne karty
   * (płyta pulpitu ma własny `kartaId`, jej szuflada to HOME-07). Jeden atrybut na dwóch
   * elementach dawałby dwa trafienia na to samo id — czyli dokładnie tę niejednoznaczność,
   * którą adresowanie po atrybucie ma usuwać.
   *
   * Trafia na sam `<Button>` w `PrzyciskDowodu`; opakowanie poniżej jest tylko stylem.
   */
  dowodId?: string;
}) {
  return (
    <Box
      data-cc-card-id={kartaId}
      sx={{
        display: 'flex',
        /*
         * BEZ `flex-wrap`. Z zawijaniem panel boczny zjeżdżał pod treść, układał się 3+1
         * i wyrównywał do lewej — płyta rosła z ~275 px do ~515 px, a jej prawa połowa
         * zostawała pusta. Makieta trzyma cztery kafle w bloku 2×2 przy PRAWEJ krawędzi.
         * Na wąskim ekranie zawijanie wraca (patrz `flexDirection` niżej).
         */
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        gap: 4,
        p: { xs: 3, md: 4 },
        borderRadius: 'var(--r-duzy)',
        background:
          'radial-gradient(90% 160% at 88% -20%, rgba(247, 177, 11, 0.3) 0%, transparent 58%),' +
          'linear-gradient(135deg, var(--atrament-3) 0%, var(--atrament) 62%)',
        color: 'var(--tekst-na-ciemnym)',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: 'var(--cien-2)',
        /*
         * ODDECH — powolny ruch świetlny z makiety. Ma dawać wrażenie, że produkt „żyje",
         * bez migotania, które męczy przy pracy przez cały dzień: 9 sekund na pełny cykl,
         * zmiana skali o 14%, przezroczystość 0.85→1. Bez tego ciemna płyta jest martwym
         * prostokątem — a to jedyny element ekranu, na który właściciel patrzy najpierw.
         *
         * `prefers-reduced-motion` zatrzymuje to globalną regułą z tokens.css; poświata
         * zostaje, znika tylko ruch.
         */
        '&::before': {
          content: '""',
          position: 'absolute',
          width: 420,
          height: 420,
          right: -140,
          top: -180,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(247, 177, 11, 0.28), transparent 66%)',
          animation: 'sygnal-oddech 9s ease-in-out infinite',
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: 4,
          background: 'linear-gradient(90deg, var(--zloto), var(--zloto-glebokie), transparent)',
        },
      }}
    >
      <Box sx={{ flexGrow: 1, minWidth: 0, zIndex: 1 }}>
        <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.55)' }}>
          {etykieta}
        </Typography>

        {pierscien ? (
          <Box className="sygnal-liczba" sx={{ my: 1 }}>
            <Pierscien
              wartosc={pierscien.wartosc}
              etykieta={pierscien.etykieta}
              rozmiar={148}
              naCiemnym
            />
          </Box>
        ) : (
          <Typography
            className="sygnal-liczba"
            sx={{
              fontFamily: 'var(--krój-display)',
              fontSize: 'clamp(40px, 7vw, 92px)',
              lineHeight: 0.95,
              color: '#fff',
              textShadow: '0 4px 40px rgba(247, 177, 11, 0.28)',
              my: 1,
              wordBreak: 'break-word',
            }}
          >
            {/* Brak wartości NIE jest zerem. Zero to zmierzony wynik, brak to niewiedza — pomylenie
                tych dwóch rzeczy zaniża wynik zamiast oznaczyć go jako nieznany. Znana wartość
                wjeżdża count-upem od 0 (AnimatedNumber); brak danych nie liczy się wcale. */}
            {wartosc !== null ? (
              <AnimatedNumberText text={wartosc} />
            ) : (
              <Box component="span" sx={{ fontSize: '0.42em', opacity: 0.72 }}>brak danych</Box>
            )}
          </Typography>
        )}

        {opis ? (
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', alignItems: 'center', color: 'rgba(255,255,255,0.72)' }}>
            {opis}
          </Stack>
        ) : null}

        <Stack direction="row" spacing={2} sx={{ mt: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          {trend}
          {stopka}
          {dowod ? (
            <Box
              sx={{
                '& .MuiButton-root': {
                  background: 'var(--zloto)',
                  color: 'var(--atrament)',
                  boxShadow: 'var(--cien-zloty)',
                  px: 2,
                  '&:hover': { background: 'var(--zloto-jasne)' },
                },
              }}
            >
              <PrzyciskDowodu dowod={dowod} kartaId={dowodId} />
            </Box>
          ) : null}
        </Stack>

        {/* Te dwa wiersze są celowo dosłowne i celowo zawsze obecne. Ekran analityczny bez
            podanego okna i źródła pokazuje liczbę, której nie da się sprawdzić ani powtórzyć. */}
        <Stack sx={{ mt: 2.5 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            Okno: {okno}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            Źródło: {zrodlo}
          </Typography>
        </Stack>
      </Box>

      {boczne ? (
        <Box
          sx={{
            display: 'grid',
            // Sztywne dwie kolumny, jak w makiecie. `auto-fit` przy szerokim rodzicu
            // rozkładał kafle w jeden rząd albo 3+1, zależnie od dostępnego miejsca.
            gridTemplateColumns: 'repeat(2, minmax(120px, 1fr))',
            gap: 1.5,
            alignContent: 'center',
            zIndex: 1,
            // `flex: none` zamiast wymuszonej szerokości: panel ma zająć tyle, ile potrzebują
            // dwie kolumny, i przykleić się do prawej krawędzi — a nie rozpychać płyty.
            flex: 'none',
            minWidth: { xs: '100%', md: 'auto' },
          }}
        >
          {boczne}
        </Box>
      ) : null}
    </Box>
  );
}

/** Mała metryka na płycie. Wartość null renderuje kreskę, nigdy zera. */
export function MiniMetryka({
  wartosc,
  jednostka,
  etykieta,
  kartaId,
}: {
  wartosc: string | null;
  jednostka?: string;
  etykieta: string;
  /** Patrz `PlytaGlowna` — ten sam adres, ta sama motywacja. */
  kartaId?: string;
}) {
  return (
    <Box
      data-cc-card-id={kartaId}
      sx={{
        px: 2,
        py: 1.5,
        borderRadius: 'var(--r-maly)',
        background: 'rgba(255,255,255,0.07)',
        border: '1px solid rgba(255,255,255,0.09)',
      }}
    >
      <Typography
        className="sygnal-liczba"
        sx={{ fontFamily: 'var(--krój-display)', fontSize: 28, lineHeight: 1, color: 'var(--zloto)' }}
      >
        {wartosc !== null ? <AnimatedNumberText text={wartosc} /> : '—'}
        {wartosc && jednostka ? (
          <Box component="span" sx={{ fontSize: 15, color: 'rgba(255,255,255,0.5)' }}>
            {jednostka}
          </Box>
        ) : null}
      </Typography>
      <Typography sx={{ fontSize: 10.5, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, color: 'rgba(255,255,255,0.52)', mt: 0.5 }}>
        {etykieta}
      </Typography>
    </Box>
  );
}
